import { SearchAreas } from "./SearchAreas";
import { PlaceLists } from "./PlaceLists";
import { useSession } from "../app/session";
import { useEffect, useState } from "react";
import type { LoroDoc } from "loro-crdt";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { Button, Checkbox, Field, Input } from "../ui";
import { SettingsSection, StatusText } from "../app/SettingsSection";
import { useCoreDoc } from "../app/useCoreDoc";
import { useDocSelector } from "../core/useDocSelector";
import { apiCall } from "../core/api";
import { listPropDefs } from "../core/ops";
import { useSignals } from "../app/signals";
import { useNow } from "../core/useNow";
import { useSignalContext } from "./useSignalContext";
import { ageText, noteText, signalLabel, valueText } from "./describe";
import {
  type Place,
  outsideRulesOn,
  deletePlace,
  readPlaces,
  readRules,
  readWeather,
  savePlace,
  setOutsideRule,
  setSignalEnabled,
  setWeather,
} from "./settingsOps";

function useDeviceLocation() {
  const [busy, setBusy] = useState(false);
  const get = (then: (lat: number, lon: number) => void) => {
    if (!navigator.geolocation) return;
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setBusy(false);
        then(Math.round(p.coords.latitude * 1e5) / 1e5, Math.round(p.coords.longitude * 1e5) / 1e5);
      },
      () => setBusy(false),
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  };
  return { busy, get };
}

/**
 * Settings → Signals (I8, ADR 0016): outside facts that move tasks up or back — every signal with its value,
 * note, source and age, each one switchable off; the weather; named places and the phone's position; how a
 * script feeds its own.
 */
export function SignalsSettings() {
  const { t } = useTranslation();
  const { handle: core, workspaceId } = useCoreDoc();
  const now = useNow();
  const sig = useSignalContext(core, workspaceId, now);
  const reload = useSignals((s) => s.load);
  const weather = useDocSelector(core, (doc) => JSON.stringify(readWeather(doc)));
  const w = JSON.parse(weather ?? "{}") as ReturnType<typeof readWeather>;
  const outsideOn = useDocSelector(core, (doc) => outsideRulesOn(readRules(doc))) ?? false;
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");
  const device = useDeviceLocation();
  const ro = core?.readOnly ?? true;
  const write = (fn: (doc: LoroDoc) => void) => {
    if (core) fn(core.doc);
  };

  const remove = async (name: string) => {
    if (!workspaceId) return;
    await apiCall(workspaceId, "DELETE", `/signals/${encodeURIComponent(name)}`).catch(() => undefined);
    void reload(workspaceId);
  };

  const names = [...new Set([...Object.keys(sig.definitions), ...Object.keys(sig.raw)])].sort();
  const latNum = lat !== "" ? Number(lat) : w.lat;
  const lonNum = lon !== "" ? Number(lon) : w.lon;
  const origin = location.origin;

  if (!workspaceId) return null;
  return (
    <SettingsSection
      id="settings-signals"
      intro={t("signals.hint")}
      summary={t("settings.summary.signals")}
      title={t("signals.title")}
      testId="settings-signals"
      folded
    >
      {names.length === 0 ? (
        <p className="text-[12.5px] text-neutral-500">{t("signals.noneYet")}</p>
      ) : (
        <ul
          className="flex flex-col divide-y divide-neutral-100 dark:divide-neutral-800"
          data-testid="signals-list"
        >
          {names.map((name) => {
            const def = sig.definitions[name];
            const rec = sig.raw[name];
            const on = def?.enabled !== false;
            const counted = name in sig.signals;
            return (
              <li
                key={name}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5"
                data-testid="signal-row"
                data-name={name}
              >
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={on}
                    disabled={ro}
                    label={t("signals.use", { name: signalLabel(name, def, t) })}
                    onChange={(v) => write((doc) => setSignalEnabled(doc, name, v))}
                  />
                  <span className={on ? "" : "text-neutral-400"}>{signalLabel(name, def, t)}</span>
                </label>
                {signalLabel(name, def, t) !== name && (
                  <span className="font-mono text-[12px] text-neutral-500">{name}</span>
                )}
                <span className="text-[13px]" data-testid="signal-value">
                  {rec ? valueText(rec.value, t, def?.unit) : t("signals.unknown")}
                </span>
                {rec && noteText(name, rec, t) && (
                  <span className="text-[12.5px] text-neutral-500">{noteText(name, rec, t)}</span>
                )}
                <StatusText tone="quiet">
                  {[rec?.source, ageText(rec, now, t), rec && !counted && on ? t("signals.expired") : null]
                    .filter(Boolean)
                    .join(" · ")}
                </StatusText>
                {!ro && (
                  <button
                    type="button"
                    aria-label={t("signals.delete", { name })}
                    onClick={() => void remove(name)}
                    className="ml-auto grid h-7 w-7 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800"
                  >
                    <Trash2 size={14} aria-hidden />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <h3 className="mt-2 text-[12px] font-semibold text-neutral-600 dark:text-neutral-300">
        {t("signals.weather.title")}
      </h3>
      <label className="flex items-center gap-3 text-sm">
        <Checkbox
          checked={w.enabled}
          disabled={ro}
          label={t("signals.weather.enable")}
          onChange={(v) => write((doc) => setWeather(doc, { ...w, enabled: v }))}
        />
        <span>{t("signals.weather.enable")}</span>
      </label>
      {w.enabled && (
        <div className="flex flex-wrap items-end gap-2" data-testid="signals-weather">
          <Field label={t("signals.lat")} htmlFor="weather-lat">
            <Input
              id="weather-lat"
              compact
              className="w-28"
              value={lat !== "" ? lat : (w.lat ?? "")}
              onChange={(e) => setLat(e.target.value)}
            />
          </Field>
          <Field label={t("signals.lon")} htmlFor="weather-lon">
            <Input
              id="weather-lon"
              compact
              className="w-28"
              value={lon !== "" ? lon : (w.lon ?? "")}
              onChange={(e) => setLon(e.target.value)}
            />
          </Field>
          <Button
            size="sm"
            variant="secondary"
            disabled={device.busy}
            onClick={() =>
              device.get((la, lo) => {
                setLat(String(la));
                setLon(String(lo));
              })
            }
          >
            {t("signals.here")}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={
              ro || latNum === null || lonNum === null || Number.isNaN(latNum) || Number.isNaN(lonNum)
            }
            onClick={() => write((doc) => setWeather(doc, { enabled: true, lat: latNum, lon: lonNum }))}
          >
            {t("signals.save")}
          </Button>
        </div>
      )}
      {w.enabled && (
        <label className="flex items-center gap-3 text-sm">
          <Checkbox
            checked={outsideOn}
            disabled={ro}
            label={t("signals.weather.outside")}
            onChange={(v) => write((doc) => setOutsideRule(doc, v))}
          />
          <span>{t("signals.weather.outside")}</span>
        </label>
      )}
      <p className="text-[12px] text-neutral-500">{t("signals.weather.hint")}</p>

      <h3 className="mt-2 text-[12px] font-semibold text-neutral-600 dark:text-neutral-300">
        {t("signals.own.title")}
      </h3>
      <p className="text-[12.5px] text-neutral-500">{t("signals.own.hint")}</p>
      <pre className="overflow-x-auto rounded-md bg-[var(--surface-2)] p-3 font-mono text-[12px]">
        {`curl -X PUT ${origin}/api/v1/signals/office.door \\
  -H "Authorization: Bearer drg_…" -H "Content-Type: application/json" \\
  -d '{"value": "open", "ttlMin": 120}'`}
      </pre>
    </SettingsSection>
  );
}

/**
 * Places (UI cleanup U5: Settings by who they affect). `mine`: the places of my own workspace, my lists, where a kind
 * of place is looked up and my phone's position — they are mine wherever I am. `shared`: the places of a shared
 * workspace, seen by everyone there; nothing outside a shared workspace.
 */
export function PlacesSettings({ scope }: { scope: "mine" | "shared" }) {
  const { t } = useTranslation();
  const { handle: core, workspaceId } = useCoreDoc();
  const placesJson = useDocSelector(core, (doc) => JSON.stringify(readPlaces(doc)));
  const places = JSON.parse(placesJson ?? "[]") as ReturnType<typeof readPlaces>;
  const [place, setPlace] = useState({ name: "", lat: "", lon: "", radius: "150" });
  const [locUrl, setLocUrl] = useState<string | null>(null);
  const device = useDeviceLocation();
  const ro = core?.readOnly ?? true;
  const session = useSession((st) => st.session);
  const space = session?.workspaces?.find((w) => w.id === workspaceId);
  const placeScope = session?.mode !== "circle" || !space ? null : space.shared ? "shared" : "mine";
  const spaceName = space?.name ?? "";
  const contextJson = useDocSelector(core, (doc) => {
    const def = listPropDefs(doc).find((d) => d.id === "context");
    return JSON.stringify(
      ((def?.spec as { values?: { id: string; label: string }[] })?.values ?? []).map((v) => ({
        id: v.id,
        label: v.label,
      })),
    );
  });
  const contextValues = JSON.parse(contextJson ?? "[]") as { id: string; label: string }[];
  const write = (fn: (doc: LoroDoc) => void) => {
    if (core) fn(core.doc);
  };
  useEffect(() => {
    if (scope !== "mine") return;
    void fetch("/api/internal/location", { credentials: "same-origin" })
      .then((r) => (r.ok ? (r.json() as Promise<{ url: string | null }>) : null))
      .then((d) => d && setLocUrl(d.url));
  }, [scope]);
  const changeLocation = (method: "POST" | "DELETE") =>
    void fetch("/api/internal/location", { method, credentials: "same-origin" })
      .then((r) => (r.ok ? (r.json() as Promise<{ url: string | null }>) : null))
      .then((d) => d && setLocUrl(d.url));

  const shared = placeScope === "shared";
  if (!workspaceId || (scope === "shared" && !shared)) return null;
  return (
    <SettingsSection
      id={scope === "shared" ? "settings-places-shared" : "settings-places"}
      intro={t("signals.places.hint")}
      title={
        scope === "shared" ? t("signals.places.sharedIn", { name: spaceName }) : t("signals.places.mineTitle")
      }
      testId={scope === "shared" ? "settings-places-shared" : "settings-places"}
      folded
    >
      {(scope === "shared") === shared && (
        <>
          <h3
            className="mt-2 text-[12px] font-semibold text-neutral-600 dark:text-neutral-300"
            data-testid="places-title"
          >
            {placeScope === "shared"
              ? t("signals.places.sharedIn", { name: spaceName })
              : placeScope === "mine"
                ? t("signals.places.mine")
                : t("signals.places.title")}
          </h3>
          <p className="text-[12.5px] text-neutral-500">{t("signals.places.hint")}</p>
          {placeScope && (
            <p className="text-[12.5px] text-neutral-500">
              {placeScope === "shared" ? t("signals.places.sharedHint") : t("signals.places.mineHint")}
            </p>
          )}
          {places.length > 0 && (
            <ul className="flex flex-col gap-1" data-testid="places-list">
              {places.map((p) => (
                <PlaceRow key={p.id} place={p} ro={ro} contexts={contextValues} write={write} />
              ))}
            </ul>
          )}
          {!ro && (
            <div className="flex flex-col gap-2" data-testid="place-new">
              {/* two rows on purpose: the inputs, then what to do with them (the button texts are long in de/es) */}
              <div className="flex items-end gap-2">
                <Field label={t("signals.places.name")} htmlFor="place-name">
                  <Input
                    id="place-name"
                    compact
                    className="w-36"
                    value={place.name}
                    placeholder={t("signals.places.namePlaceholder")}
                    onChange={(e) => setPlace({ ...place, name: e.target.value })}
                  />
                </Field>
                <Field label={t("signals.lat")} htmlFor="place-lat">
                  <Input
                    id="place-lat"
                    compact
                    className="w-28"
                    value={place.lat}
                    onChange={(e) => setPlace({ ...place, lat: e.target.value })}
                  />
                </Field>
                <Field label={t("signals.lon")} htmlFor="place-lon">
                  <Input
                    id="place-lon"
                    compact
                    className="w-28"
                    value={place.lon}
                    onChange={(e) => setPlace({ ...place, lon: e.target.value })}
                  />
                </Field>
                <Field label={t("signals.places.radius")} htmlFor="place-radius">
                  <Input
                    id="place-radius"
                    compact
                    type="number"
                    className="w-24"
                    value={place.radius}
                    onChange={(e) => setPlace({ ...place, radius: e.target.value })}
                  />
                </Field>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={device.busy}
                  onClick={() =>
                    device.get((la, lo) => setPlace({ ...place, lat: String(la), lon: String(lo) }))
                  }
                >
                  {t("signals.here")}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={
                    !place.name.trim() ||
                    place.lat === "" ||
                    place.lon === "" ||
                    Number.isNaN(Number(place.lat)) ||
                    Number.isNaN(Number(place.lon))
                  }
                  onClick={() => {
                    write((doc) =>
                      savePlace(doc, {
                        name: place.name,
                        lat: Number(place.lat),
                        lon: Number(place.lon),
                        radius: Math.min(50000, Math.max(10, Number(place.radius) || 150)),
                      }),
                    );
                    setPlace({ name: "", lat: "", lon: "", radius: "150" });
                  }}
                  data-testid="place-save"
                >
                  {t("signals.places.add")}
                </Button>
              </div>
            </div>
          )}
          <PlaceLists core={core} places={places} ro={ro} write={write} />
        </>
      )}
      {scope === "mine" && (
        <>
          {/* where a kind of place is looked up: beside the places, not after the phone's position (UI cleanup U3) */}
          <SearchAreas />

          <h3 className="mt-2 text-[12px] font-semibold text-neutral-600 dark:text-neutral-300">
            {t("signals.phone.title")}
          </h3>
          <p className="text-[12.5px] text-neutral-500">{t("signals.phone.hint")}</p>
          {locUrl ? (
            <div className="flex flex-col gap-2" data-testid="location-address">
              <Input
                compact
                readOnly
                value={locUrl}
                onFocus={(e) => e.target.select()}
                className="font-mono"
                aria-label={t("signals.phone.address")}
              />
              <div className="flex gap-2">
                <Button size="sm" variant="secondary" onClick={() => changeLocation("POST")}>
                  {t("signals.phone.renew")}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => changeLocation("DELETE")}>
                  {t("signals.phone.stop")}
                </Button>
              </div>
              <ol className="list-decimal pl-5 text-[13px] leading-relaxed">
                <li>{t("signals.phone.owntracks")}</li>
                <li>{t("signals.phone.ha")}</li>
              </ol>
            </div>
          ) : (
            <div>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => changeLocation("POST")}
                data-testid="location-make"
              >
                {t("signals.phone.make")}
              </Button>
            </div>
          )}
        </>
      )}
    </SettingsSection>
  );
}

/** One place, with its reminder settings (I9) behind "Reminders…". */
function PlaceRow({
  place,
  ro,
  contexts,
  write,
}: {
  place: Place;
  ro: boolean;
  contexts: { id: string; label: string }[];
  write(fn: (doc: LoroDoc) => void): void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string[]>(place.contexts ?? []);
  const [wait, setWait] = useState(String(place.waitMin ?? 2));
  const [gap, setGap] = useState(String(place.gapHours ?? 4));
  return (
    <li className="flex flex-col gap-1.5 text-sm" data-testid="place-row" data-name={place.name}>
      <div className="flex items-center gap-3">
        <span>{place.name}</span>
        <span className="font-mono text-[12px] text-neutral-500">
          {place.lat}, {place.lon} · {t("signals.places.radiusM", { n: place.radius })}
        </span>
        {(place.contexts?.length ?? 0) > 0 && (
          <StatusText tone="quiet">{(place.contexts ?? []).map((c) => `@${c}`).join(" ")}</StatusText>
        )}
        <Button
          size="sm"
          variant="secondary"
          className="ml-auto"
          onClick={() => setOpen(!open)}
          data-testid="place-reminders"
        >
          {t("signals.places.reminders")}
        </Button>
        {!ro && (
          <button
            type="button"
            aria-label={t("signals.places.delete", { name: place.name })}
            onClick={() => write((doc) => deletePlace(doc, place.id))}
            className="grid h-7 w-7 place-items-center rounded-sm text-neutral-400 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800"
          >
            <Trash2 size={14} aria-hidden />
          </button>
        )}
      </div>
      {open && (
        <div
          className="ml-3 flex flex-col gap-2 border-l border-neutral-200 pl-3 dark:border-neutral-800"
          data-testid="place-reminder-form"
        >
          <p className="text-[12.5px] text-neutral-500">{t("signals.places.remindersHint")}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {contexts.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-[13px]">
                <Checkbox
                  checked={chosen.includes(c.id)}
                  disabled={ro}
                  label={c.label}
                  onChange={(v) => setChosen(v ? [...chosen, c.id] : chosen.filter((x) => x !== c.id))}
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <Field label={t("signals.places.wait")} htmlFor={`wait-${place.id}`}>
              <Input
                id={`wait-${place.id}`}
                compact
                type="number"
                className="w-20"
                value={wait}
                onChange={(e) => setWait(e.target.value)}
              />
            </Field>
            <Field label={t("signals.places.gap")} htmlFor={`gap-${place.id}`}>
              <Input
                id={`gap-${place.id}`}
                compact
                type="number"
                className="w-20"
                value={gap}
                onChange={(e) => setGap(e.target.value)}
              />
            </Field>
            <Button
              size="sm"
              variant="secondary"
              disabled={ro}
              onClick={() => {
                write((doc) =>
                  savePlace(doc, {
                    ...place,
                    contexts: chosen,
                    waitMin: Math.min(120, Math.max(0, Number(wait) || 0)),
                    gapHours: Math.min(168, Math.max(0, Number(gap) || 0)),
                  }),
                );
                setOpen(false);
              }}
              data-testid="place-reminders-save"
            >
              {t("signals.save")}
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}
